/**
 * @component NewsListPage
 * @description 공지/소식 콘텐츠를 페이징된 리스트로 제공합니다.
 *              뉴스 등록·수정·삭제는 관리자 콘솔(/admin/news)에서 수행합니다.
 * @usage App 라우팅에서 `/news/index.html` 경로일 때 렌더링됩니다.
 */

import { useMemo, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import PageHero from '../../components/common/PageHero';
import { pageMeta } from '../../constants/navigation';

const NEWS_PAGE_SIZE = 10;

// [기능] 현재 페이지 주변의 페이지 번호 목록을 계산합니다. 전체가 적으면 모두 반환합니다.
function getVisiblePageIndexes(currentPage, totalPages) {
  const windowSize = 5;
  if (totalPages <= windowSize) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const half = Math.floor(windowSize / 2);
  let start = Math.max(currentPage - half, 0);
  let end = start + windowSize - 1;

  if (end > totalPages - 1) {
    end = totalPages - 1;
    start = end - windowSize + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export default function NewsListPage({ onNavigate }) {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  // [기능] 백엔드 API에서 뉴스 목록을 가져옵니다.
  useEffect(() => {
    let isActive = true;
    fetch('/api/news')
      .then((r) => r.json())
      .then((data) => {
        if (isActive) setNewsItems(data);
      })
      .catch(() => {
        // [비즈니스] 목록을 불러오지 못해도 페이지 틀은 정상 노출되어야 합니다.
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, []);

  const totalPages = Math.ceil(newsItems.length / NEWS_PAGE_SIZE);
  const pageIndexes = getVisiblePageIndexes(currentPage, totalPages);
  const isFirstPage = currentPage <= 0;
  const isLastPage = totalPages === 0 || currentPage >= totalPages - 1;

  const visibleNewsItems = useMemo(() => {
    const startIndex = currentPage * NEWS_PAGE_SIZE;
    return newsItems.slice(startIndex, startIndex + NEWS_PAGE_SIZE);
  }, [newsItems, currentPage]);

  const goToPage = (pageIndex) => {
    setCurrentPage(Math.min(Math.max(pageIndex, 0), totalPages - 1));
  };

  const goToDetail = (slug) => {
    onNavigate(`/news/${slug}.html`);
  };

  // [기능] 키보드로 목록 행을 선택했을 때도 상세로 이동할 수 있게 합니다.
  const handleRowKeyDown = (event, slug) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      goToDetail(slug);
    }
  };

  return (
    <>
      <PageHero title={pageMeta.newsList.title} trail={pageMeta.newsList.trail} className="news-page-hero" />
      <section className="section">
        <div className="container">
          <div className="news-list-panel fade-in">

            <div className="news-status-row">
              <p className="news-page-status">총 {loading ? '…' : newsItems.length}건</p>
            </div>

            <div className="news-list-table-wrap">
              <table className="news-list-table">
                <tbody>
                  {visibleNewsItems.map((item, index) => {
                    const itemNumber = newsItems.length - (currentPage * NEWS_PAGE_SIZE + index);

                    return (
                      <tr
                        key={`${item.slug}-${currentPage}-${index}`}
                        className="news-list-row"
                        role="button"
                        tabIndex={0}
                        onClick={() => goToDetail(item.slug)}
                        onKeyDown={(event) => handleRowKeyDown(event, item.slug)}
                      >
                        <td>{itemNumber}</td>
                        <td>
                          <span className="news-category-badge">{item.category}</span>
                        </td>
                        <td className="news-list-title">
                          <span>{item.title}</span>
                        </td>
                        <td>{item.date}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 ? (
              <nav className="news-pagination" aria-label="뉴스 목록 페이지 이동">
                <div className="news-page-controls">
                  <button
                    type="button"
                    className="page-nav-btn page-nav-edge"
                    onClick={() => goToPage(0)}
                    disabled={isFirstPage}
                    aria-label="첫 페이지"
                  >
                    <ChevronsLeft size={15} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="page-nav-btn page-nav-text"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={isFirstPage}
                    aria-label="이전 페이지"
                  >
                    <ChevronLeft size={14} strokeWidth={2} />
                  </button>
                  {pageIndexes.map((pageIndex) => (
                    <button
                      key={pageIndex}
                      type="button"
                      className={`page-number-button ${pageIndex === currentPage ? 'is-active' : ''}`}
                      onClick={() => goToPage(pageIndex)}
                      aria-current={pageIndex === currentPage ? 'page' : undefined}
                    >
                      {pageIndex + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="page-nav-btn page-nav-text"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={isLastPage}
                    aria-label="다음 페이지"
                  >
                    <ChevronRight size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="page-nav-btn page-nav-edge"
                    onClick={() => goToPage(totalPages - 1)}
                    disabled={isLastPage}
                    aria-label="마지막 페이지"
                  >
                    <ChevronsRight size={15} strokeWidth={2} />
                  </button>
                </div>
              </nav>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}
